import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  HvInput,
  HvGrid,
  HvCheckBox,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import { useFormikContext } from "formik";
import { Edit } from "@hitachivantara/uikit-react-icons";
import { CameraHidePassword } from "lib/api";
import useStyles from "./styles";

interface RTSPProps {
  mode: string;
}

const RTSPTypeContent: React.FC<RTSPProps> = () => {
  const { t } = useTranslation("cameraAddEdit");
  const classes = useStyles();
  const {
    values,
    setFieldTouched,
    setFieldValue,
    status,
    setStatus,
    errors,
    touched,
  } = useFormikContext<CameraDetailsFormData>();
  const { serverSideErrors = {} } = status;
  const parseStatus = (field: keyof CameraDetailsFormData) =>
    serverSideErrors[field] || (errors[field] && touched[field])
      ? "invalid"
      : "valid";

  const parseStatusMessage = (field: keyof CameraDetailsFormData) => {
    if (serverSideErrors[field]) {
      return serverSideErrors[field];
    }

    return errors[field] && touched[field] ? errors[field] : "";
  };

  const setValueOnForm = useCallback(
    (property: string, selection: unknown) => {
      setFieldValue(property, selection);
      // clearServerSideErrors
      setStatus({});
    },
    [setFieldValue, setStatus]
  );
  return (
    <HvGrid container className={(classes.root, classes.item)}>
      <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
        <HvInput
          className={classes.input}
          name="ipAddress"
          label={t("cameraType.fields.ipAddress")}
          placeholder={t("cameraType.fields.ipAddressPlaceholder")}
          inputProps={{
            autoComplete: "off",
          }}
          value={values.ipAddress || ""}
          status={parseStatus("ipAddress")}
          statusMessage={parseStatusMessage("ipAddress")}
          onBlur={() => setFieldTouched("ipAddress", true, false)}
          onChange={(evt, value) => {
            setValueOnForm("ipAddress", value);
          }}
        />
      </HvGrid>
      <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
        <HvInput
          className={classes.input}
          name="port"
          label={t("cameraType.fields.port")}
          description={t("cameraType.fields.portDescription")}
          placeholder={t("cameraType.fields.portPlaceholder")}
          inputProps={{
            autoComplete: "off",
          }}
          value={values.port || ""}
          status={parseStatus("port")}
          statusMessage={parseStatusMessage("port")}
          onBlur={() => setFieldTouched("port", true, false)}
          onChange={(evt, value) => {
            setValueOnForm("port", value);
          }}
        />
      </HvGrid>
      <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
        <HvInput
          className={classes.input}
          name="username"
          label={t("cameraType.fields.username")}
          placeholder={t("cameraType.fields.usernamePlaceholder")}
          inputProps={{
            autoComplete: "off",
          }}
          value={values.username || ""}
          status={parseStatus("username")}
          statusMessage={parseStatusMessage("username")}
          onBlur={() => setFieldTouched("username", true, false)}
          onChange={(evt, value) => {
            setValueOnForm("username", value);
          }}
          required={values.password !== ""}
        />
      </HvGrid>
      <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
        <HvInput
          type="password"
          className={classes.input}
          name="password"
          label={t("cameraType.fields.password")}
          placeholder={t("cameraType.fields.passwordPlaceholder")}
          inputProps={{
            autoComplete: "off",
          }}
          readOnly={values.password === CameraHidePassword}
          value={values.password || ""}
          status={parseStatus("password")}
          statusMessage={parseStatusMessage("password")}
          onBlur={() => setFieldTouched("password", true, false)}
          onChange={(evt, value) => {
            setValueOnForm("password", value);
          }}
          required={values.username !== ""}
          endAdornment={
            <>
              {values.password === CameraHidePassword ? (
                <Edit
                  className={classes.passwordEdit}
                  onClick={() => {
                    setValueOnForm("password", "");
                    setFieldTouched("password", true, false);
                  }}
                />
              ) : (
                <></>
              )}
            </>
          }
        />
      </HvGrid>
      <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
        <HvInput
          type="customStreamUrl"
          className={classes.input}
          name="customStreamUrl"
          label={t("cameraType.fields.customStreamUrl")}
          description={t("cameraType.fields.customStreamUrlDescription")}
          placeholder={t("cameraType.fields.customStreamUrlPlaceholder")}
          inputProps={{
            autoComplete: "off",
          }}
          value={values.customStreamUrl || ""}
          status={parseStatus("customStreamUrl")}
          statusMessage={parseStatusMessage("customStreamUrl")}
          onBlur={() => setFieldTouched("customStreamUrl", true, false)}
          onChange={(evt, value) => {
            setValueOnForm("customStreamUrl", value);
          }}
        />
      </HvGrid>
      <HvGrid xs={12} md={6} className={classes.gridPaddingOverRide}>
        <HvTypography
          variant="highlightText"
          className={classes.cameraSpecificities}
          paragraph
        >
          {t("cameraType.fields.cameraSpecificities")}
        </HvTypography>
        <HvCheckBox
          label={t("cameraType.fields.overSSL")}
          value={values.overSSL || false}
          checked={values.overSSL || false}
          onBlur={() => setFieldTouched("overSSL", true, false)}
          onChange={(evt, value) => setValueOnForm("overSSL", value)}
        />
        <HvCheckBox
          label={t("cameraType.fields.microphone")}
          value={values.microphone || false}
          checked={values.microphone || false}
          onBlur={() => setFieldTouched("microphone", true, false)}
          onChange={(evt, value) => {
            setValueOnForm("microphone", value);
          }}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default RTSPTypeContent;
